import { useEffect, useRef } from "react";
import { Loader2, Sparkles, User } from "lucide-react";
import { useAgentHistory } from "../api/queries";
import type { ChatTurn } from "../api/types";
import { AgentMarkdown } from "./AgentMarkdown";
import { SuggestionPills } from "./SuggestionPills";

interface Props {
  conv: string;
  onPickSuggestedAction?: (prefill: string) => void;
}

/**
 * Transcript of the agent dialog.
 *
 * Pattern follows Claude.ai / ChatGPT, not iMessage:
 *  - Agent turns render as flowing markdown prose with no bubble — long
 *    answers and lists need breathing room. The bubble idiom works for
 *    short conversational lines (WhatsApp) but breaks down for
 *    structured replies.
 *  - User turns are right-aligned, tinted ink, compact — the user
 *    rarely writes long-form so a chip-style block is fine.
 *  - Each turn carries a small role marker (icon + label) on the side
 *    matching the message — Claude.ai-style.
 *
 * Markdown is rendered via react-markdown + remark-gfm (tables,
 * strikethrough, autolinks). Tailwind Typography provides the prose
 * defaults; we constrain to `prose-sm` and tweak for the brand.
 */
export function ChatHistory({ conv, onPickSuggestedAction }: Props) {
  const { data, isLoading } = useAgentHistory(conv);
  const scrollRef = useRef<HTMLDivElement>(null);
  const unresolvedIndex = findLatestUnresolvedAgentTurnIndex(data ?? []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [data]);

  if (isLoading) {
    return (
      <div className="text-sm text-muted dark:text-muted-dark px-6 py-6 flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Chargement de l'historique…
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-sm text-muted dark:text-muted-dark px-6 py-6">
        Aucun message dans cette conversation. Tapez quelque chose à l'agent
        pour commencer.
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      className="flex-1 overflow-y-auto px-6 py-6 space-y-6"
    >
      {data.map((turn, idx) => {
        if (turn.role === "user") {
          return (
            <div
              key={`${turn.occurred_at}-${idx}`}
              className="flex flex-col items-end gap-1.5"
            >
              <div className="flex items-center gap-1.5 text-xs text-muted dark:text-muted-dark">
                <span>Vous</span>
                <User className="h-3 w-3" />
              </div>
              <div
                className="max-w-[85%] px-4 py-2.5 rounded-2xl rounded-br-md bg-ink text-white text-sm leading-relaxed whitespace-pre-wrap break-words"
                title={turn.occurred_at}
              >
                {turn.text}
              </div>
            </div>
          );
        }
        const voix = turn.voix && turn.voix.trim();
        const details = (turn.details && turn.details.trim()) || turn.text;
        const showVoixSeparately = voix && voix !== details.trim();
        const showSuggestedActions = (
          idx === unresolvedIndex &&
          !!onPickSuggestedAction &&
          (turn.suggested_actions?.length ?? 0) > 0
        );
        return (
          <div
            key={`${turn.occurred_at}-${idx}`}
            className="flex flex-col items-start gap-1.5"
          >
            <div className="flex items-center gap-1.5 text-xs text-muted dark:text-muted-dark">
              <Sparkles className="h-3 w-3 text-chip-orange" />
              <span>Agent</span>
            </div>
            {showVoixSeparately && (
              <blockquote
                className="pl-3 border-l-4 border-chip-orange italic text-ink dark:text-ink-dark leading-snug"
                title={turn.occurred_at}
              >
                {voix}
              </blockquote>
            )}
            <div title={turn.occurred_at}>
              <AgentMarkdown markdown={details} className="max-w-full overflow-x-auto" />
            </div>
            {showSuggestedActions && (
              <SuggestionPills
                suggestions={turn.suggested_actions ?? []}
                onPick={onPickSuggestedAction}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function findLatestUnresolvedAgentTurnIndex(turns: ChatTurn[]): number {
  for (let idx = turns.length - 1; idx >= 0; idx -= 1) {
    const turn = turns[idx];
    if (turn.role === "user") {
      return -1;
    }
    if (turn.role === "agent" && (turn.suggested_actions?.length ?? 0) > 0) {
      return idx;
    }
  }
  return -1;
}
